/**
 * How an account's served identifier reads (TMMAPI-0165). `AccountResponse` has no
 * `identifierDisplay`; the same masking the server uses for a wire (`CR05 •••• 4066`) is applied
 * to the served `externalIdentifier` and `externalIdentifierKind`. Null when none is served.
 */
export function identifierDisplay(
  identifier: string | null | undefined,
  kind: string | null | undefined,
): string | null {
  if (identifier == null || identifier.length === 0) return null;
  const id = identifier.replace(/\s/g, '').toUpperCase();
  if (kind === 'IBAN' && id.length >= 8) return `${id.slice(0, 4)} •••• ${id.slice(-4)}`;
  if (kind === 'CLABE' && id.length >= 7) return `${id.slice(0, 3)} •••• ${id.slice(-4)}`;
  if (id.length >= 4) return `•••• ${id.slice(-4)}`;
  return null;
}

/** IBAN, CLABE or ACCOUNT — the kinds the edit form names. Anything else is unnamed. */
export function servedIdentifierKind(kind: string | null | undefined): string | null {
  return kind === 'IBAN' || kind === 'CLABE' || kind === 'ACCOUNT' ? kind : null;
}

/** Compact form sent as `externalIdentifier` (the server stores it without spaces). */
export function compactIdentifier(value: string): string {
  return value.replace(/\s/g, '').toUpperCase();
}

/** Title case, as the server names a payee taken from a wire. */
export function titleCaseWireName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter((part) => part.length > 0)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ');
}

/**
 * Last 4 to pre-fill in the own-account flow (TMMAPP-0139 item 7), the same from every entry: a
 * 4-digit mask, else the last four characters of the served identifier when they are digits
 * (`CR62010200009546` → `9546`), else empty.
 */
export function prefillLast4(
  mask: string | null | undefined,
  identifier: string | null | undefined,
): string {
  if (mask != null && /^\d{4}$/.test(mask)) return mask;
  const tail = compactIdentifier(identifier ?? '').slice(-4);
  return /^\d{4}$/.test(tail) ? tail : '';
}
